/**
 * WEB COPY of LetterSavedScreen.tsx — picked by the web bundler ahead of the
 * mobile file (`.web.tsx` resolves first); Metro never sees it.
 *
 * Same promise, same buttons. What differs is recordings: the browser has
 * no encrypted registry and no player, so this screen lists what the server
 * knows — uploaded recordings (letter tier+) and the on-device notes the
 * phone sends when a reading is kept ("2:14 · on Matt's iPhone"). Readers
 * without delivery see the honest next step next to a phone recording: add
 * delivery to keep it in the cloud. "Read it aloud for them" opens the
 * record-offer screen, which on the web explains how to record in the app.
 * Export copies the letter to the clipboard instead of opening a share
 * sheet, which desktop browsers do not have. When LetterSavedScreen.tsx
 * changes, re-apply the change here.
 */
import { useFocusEffect, useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { letterToLovedOnesService } from '../../services/letterToLovedOnesInfo';
import {
  letterDeviceRecordingNotes,
  CloudRecordingSummary,
  DeviceRecordingNote,
} from '../../services/media/letterDeviceRecordingNotes';
import { onboardingService } from '../../services/onboarding';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';

import { shortDeviceName } from './LettersHomeScreen.web';

const logger = createLogger('LetterSaved');

type SavedRouteProp = RouteProp<{ params: { letterId: string } }, 'params'>;

const PROMISES: { icon: any; text: string }[] = [
  {
    icon: require('../../assets/icons/BlueLock.png'),
    text: 'It stays in your vault as long as you want it there.',
  },
  {
    icon: require('../../assets/icons/copy.png'),
    text: 'Export or share it any time — it is yours.',
  },
  {
    icon: require('../../assets/icons/check.png'),
    text: 'Rewrite it whenever you like — this is a draft of a life.',
  },
];

const mmss = (secs: number) =>
  `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

export const LetterSavedScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<SavedRouteProp>();
  const { letterId } = route.params;
  const { currentTier } = useSubscription();
  const hasDeliveryTier = ['letter', 'basic', 'plus', 'premium'].includes(currentTier);

  const [salutation, setSalutation] = useState('');
  const [deliveryTrigger, setDeliveryTrigger] = useState<string | null>(null);
  const [letterContent, setLetterContent] = useState('');
  const [cloudRecordings, setCloudRecordings] = useState<CloudRecordingSummary[]>([]);
  const [phoneNotes, setPhoneNotes] = useState<DeviceRecordingNote[]>([]);

  useEffect(() => {
    // A saved letter moves the flow past the hook — resume lands on Levels.
    onboardingService.setLetterStep('levels');
    const load = async () => {
      try {
        const letter = await letterToLovedOnesService.getLetterToLovedOnesById(letterId);
        setSalutation(letter?.salutation || letter?.recipientName || '');
        setLetterContent(letter?.letterContent || '');
        setDeliveryTrigger(letter?.deliveryTrigger || null);
      } catch (error) {
        logger.error('Failed to load saved letter:', error);
      }
    };
    load();
  }, [letterId]);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      const load = async () => {
        const [cloud, notes] = await Promise.all([
          letterDeviceRecordingNotes.listCloud(letterId),
          letterDeviceRecordingNotes.list(letterId),
        ]);
        if (cancelled) return;
        setCloudRecordings(cloud);
        setPhoneNotes(notes);
      };
      load();
      return () => {
        cancelled = true;
      };
    }, [letterId]),
  );

  const handleExport = async () => {
    const header = salutation ? `Dear ${salutation},\n\n` : '';
    try {
      // The app's tsconfig has no DOM lib; reach the clipboard through globalThis.
      const clipboard = (
        globalThis as unknown as {
          navigator?: { clipboard?: { writeText(text: string): Promise<void> } };
        }
      ).navigator?.clipboard;
      if (!clipboard) throw new Error('Clipboard unavailable');
      await clipboard.writeText(`${header}${letterContent}`);
      Alert.alert('Copied', 'Your letter is on the clipboard — paste it anywhere you like.');
    } catch (error) {
      logger.error('Copy failed:', error);
      Alert.alert('Error', 'Could not copy the letter. Please try again.');
    }
  };

  const recordingCount = cloudRecordings.length + phoneNotes.length;

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.sheet}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <View style={styles.hero}>
            <Image
              source={require('../../assets/icons/check.png')}
              style={styles.heroIcon}
            />
            <Text style={styles.title}>That's the hard part done.</Text>
            <Text style={styles.subtitle}>
              Your letter{salutation ? ` to ${salutation}` : ''} is saved and
              encrypted.
            </Text>
          </View>

          <View style={styles.promiseCard}>
            <Text style={styles.promiseLabel}>Free, forever</Text>
            {PROMISES.map((p) => (
              <View key={p.text} style={styles.promiseRow}>
                <Image source={p.icon} style={styles.promiseIcon} />
                <Text style={styles.promiseText}>{p.text}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.footnote}>
            No card, no trial clock. We'll ask about delivery when you're ready
            for it.
          </Text>

          {recordingCount > 0 && (
            <View style={styles.recordingsCard}>
              <Text style={styles.recordingsLabel}>Your recordings</Text>
              {cloudRecordings.map((rec) => (
                <View key={rec.id} style={styles.recordingRow}>
                  <View style={styles.recBadge}>
                    <Image
                      source={require('../../assets/icons/communication.png')}
                      style={styles.recBadgeIcon}
                    />
                  </View>
                  <View style={styles.recInfo}>
                    <Text style={styles.recTitle}>Read aloud by you</Text>
                    <Text style={styles.recSub}>
                      {mmss(rec.durationSecs)} · encrypted · backed up · plays on your phone
                    </Text>
                  </View>
                </View>
              ))}
              {phoneNotes.map((note) => (
                <View key={note.id} style={styles.recordingRow}>
                  <View style={styles.recBadge}>
                    <Image
                      source={require('../../assets/icons/communication.png')}
                      style={styles.recBadgeIcon}
                    />
                  </View>
                  <View style={styles.recInfo}>
                    <Text style={styles.recTitle}>Read aloud by you</Text>
                    <Text style={styles.recSub}>
                      {mmss(note.durationSecs)} · encrypted · on {shortDeviceName(note.deviceName)}
                    </Text>
                  </View>
                </View>
              ))}
              {phoneNotes.length > 0 && !hasDeliveryTier && (
                <TouchableOpacity
                  style={styles.recHint}
                  onPress={() => navigation.navigate('LetterPaywall', { letterId })}
                  accessibilityRole="button"
                  accessibilityLabel="Add delivery to keep your recording in the cloud"
                >
                  <Text style={styles.recHintText}>
                    Your reading lives only on that phone. Add delivery to keep
                    it in the cloud, where it can reach them.
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {hasDeliveryTier && (
            <TouchableOpacity
              style={styles.primaryButton}
              onPress={() => navigation.navigate('LetterDelivery', { letterId })}
              accessibilityRole="button"
              accessibilityLabel={deliveryTrigger ? 'Update delivery' : 'Set up delivery'}
              accessibilityHint="Double tap to choose who receives this letter and what releases it"
            >
              <Text style={styles.primaryButtonText}>
                {deliveryTrigger ? 'Update delivery' : 'Set up delivery'}
              </Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={hasDeliveryTier ? styles.secondaryButton : styles.primaryButton}
            onPress={() => navigation.navigate('LetterRecordOffer', { letterId })}
            accessibilityRole="button"
            accessibilityLabel="Read it aloud for them"
            accessibilityHint="Double tap to see how to record yourself reading this letter"
          >
            <Text style={hasDeliveryTier ? styles.secondaryButtonText : styles.primaryButtonText}>
              {recordingCount > 0 ? 'Record another reading' : 'Read it aloud for them'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={() => navigation.navigate('LettersHome')}
            accessibilityRole="button"
            accessibilityLabel="Back to your letters"
          >
            <Text style={styles.secondaryButtonText}>Back to your letters</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.exportButton}
            onPress={handleExport}
            accessibilityRole="button"
            accessibilityLabel="Copy my letter"
            accessibilityHint="Double tap to copy your letter to the clipboard"
          >
            <Text style={styles.exportButtonText}>Copy my letter</Text>
          </TouchableOpacity>

          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    marginTop: s(56),
    paddingTop: ms(28),
    paddingHorizontal: ms(16),
  },
  hero: {
    alignItems: 'center',
    marginBottom: ms(20),
  },
  heroIcon: {
    width: s(56),
    height: s(56),
    tintColor: '#29BFC0',
    marginBottom: ms(12),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    textAlign: 'center',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(15, 0.3),
    lineHeight: ms(22, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  promiseCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(16),
    marginBottom: ms(14),
  },
  promiseLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: ms(12),
  },
  promiseRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: ms(10),
    marginBottom: ms(10),
  },
  promiseIcon: {
    width: s(18),
    height: s(18),
    tintColor: '#29BFC0',
    marginTop: ms(2),
  },
  promiseText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#1C2C4F',
  },
  footnote: {
    fontSize: ms(13, 0.3),
    lineHeight: ms(19, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(18),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
    marginBottom: ms(10),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  secondaryButton: {
    alignItems: 'center',
    paddingVertical: ms(12),
    borderRadius: ms(25),
    borderWidth: ms(1.5),
    borderColor: '#29BFC0',
    backgroundColor: '#FFFFFF',
    marginBottom: ms(4),
  },
  secondaryButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  recordingsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(14),
    marginBottom: ms(14),
  },
  recordingsLabel: {
    fontSize: ms(12, 0.3),
    fontWeight: '600',
    color: '#A9A9A9',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: ms(10),
  },
  recordingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
    paddingVertical: ms(6),
  },
  recBadge: {
    width: s(40),
    height: s(40),
    borderRadius: s(20),
    backgroundColor: '#DFF2F1',
    alignItems: 'center',
    justifyContent: 'center',
  },
  recBadgeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#1D5C59',
  },
  recInfo: {
    flex: 1,
  },
  recTitle: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  recSub: {
    fontSize: ms(12, 0.3),
    color: '#6B7280',
  },
  recHint: {
    marginTop: ms(8),
    backgroundColor: '#FFF0E0',
    borderRadius: ms(10),
    padding: ms(10),
  },
  recHintText: {
    fontSize: ms(12.5, 0.3),
    lineHeight: ms(18, 0.3),
    color: '#1C2C4F',
  },
  exportButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
  },
  exportButtonText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  bottomPadding: {
    height: s(40),
  },
});

export default LetterSavedScreen;
